// Persist React Query cache to localStorage so returning users see their
// previous content instantly — no skeletons, no waiting for the network.
// Cached entries are invalidated automatically when they go stale.
import type { QueryClient } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

export const QUERY_CACHE_KEY = "jozori-query-cache-v1";
const CACHE_KEY = QUERY_CACHE_KEY;
const QUERY_CACHE_BUSTER = "v2-story-stability-20260523";

// Query-key fragments that must NEVER be persisted to localStorage.
// These either contain signed URLs, per-user data, or otherwise risk
// leaking across sessions if restored from disk on a different user.
const NON_PERSISTABLE = [
  "auth",
  "session",
  "signed-url",
  "watch",         // contains signed media URLs (short TTL)
  "subscription",  // user-specific billing state
  "me",            // per-user profile
  "profile",
  "child",         // per-child progress / favorites
  "family",
  "billing",
  "admin",
];
const MAX_AGE = 24 * 60 * 60 * 1000; // 24h

export function setupQueryPersistence(queryClient: QueryClient) {
  if (typeof window === "undefined") return;
  try {
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      key: CACHE_KEY,
      throttleTime: 1500,
      // Skip persisting massive or sensitive payloads if needed via serialize hook.
    });

    persistQueryClient({
      queryClient,
      persister,
      maxAge: MAX_AGE,
      // Bump buster to invalidate everyone's cache after a breaking change.
      buster: QUERY_CACHE_BUSTER,
      dehydrateOptions: {
        // Don't persist failed queries or live-only data (auth/session).
        shouldDehydrateQuery: (q) => {
          if (q.state.status !== "success") return false;
          const key = JSON.stringify(q.queryKey).toLowerCase();
          if (NON_PERSISTABLE.some((frag) => key.includes(frag))) return false;
          return true;
        },
      },
    });
  } catch (err) {
    // Quota errors / private mode — fail silently, app still works.
    console.warn("[jozori] query persistence unavailable", err);
  }
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return; // only in production builds

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => console.warn("[jozori] sw registration failed", err));
  });
}

/**
 * Respect Save-Data and 2G connections — disables eager link preloading
 * so we don't burn the user's data plan on weak networks.
 */
export function shouldPreloadAggressively(): boolean {
  if (typeof navigator === "undefined") return true;
  const conn = (navigator as any).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  if (conn.effectiveType && /(^| )(slow-2g|2g)( |$)/.test(conn.effectiveType)) return false;
  return true;
}
